import { ChangeDetectorRef, Component, ElementRef, Input, OnChanges, ViewChild } from '@angular/core';
import { HttpClient, HttpDownloadProgressEvent, HttpEventType } from '@angular/common/http';
import { NavigationEnd, Router } from '@angular/router';
import { firstValueFrom, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { AlertController } from '@ionic/angular';
import { DataService } from '../data.service';
import { KeretService } from '../keret.service';
import { NaploService } from '../naplo/naplo.service';
import { NaploMenuService } from '../naplo/naplo-menu.service';
import { menupontCel } from '../naplo/naplo-menu';
import { BerService } from '../ber/ber.service';
import { BER_FOMENUK, lathatoPont } from '../ber/ber-menu';

/**
 *  KONRÁD NYUSZI – a beépített segéd lebegő ablaka.
 *
 *  Minden oldalon ott van (az app-keret teszi ki), de csak akkor látszik, ha az intézménynél
 *  az admin bekapcsolta. A kérdést a kiszolgáló válaszolja meg (`/api/konrad/cseveg`); innen
 *  csak a felhasználó MENÜJÉT küldjük mellé, hogy Konrád oda navigáljon, ahová a felhasználó
 *  tényleg mehet. Az írásos műveletek kártyán jönnek vissza: csak a Jóváhagyás gombra futnak le.
 *
 *  A BESZÉLGETÉS MEGMARAD (localStorage): oldalváltás és frissítés után folytatható. Ha a frissítés
 *  egy épp futó kérdést szakított meg, Konrád jelzi, és egy gombbal újrakérdezhető.
 *  ALRENDSZERENKÉNT (SULINFORM / Könyvelés / Bér) más a szerepe: a kiszolgáló a `mod` szerint választ
 *  rendszerutasítást, kézikönyv-fejezetet és műveleteket. A Könyvelés és a Bér a bal sáv helyett
 *  saját főmenüt használ – onnan küldjük a menüpontokat (leírással), és a kiválasztott ügyfelet.
 *  A beszélgetés alrendszerenként külön tárolódik.
 *
 *  Az „Új beszélgetés” gomb és a bezárás (X, megerősítés után) azonnal megszakítja a gondolkodást –
 *  a kérés bontása a kiszolgálón a modell-hívást is leállítja – és elfelejti a beszélgetést.
 */
interface Uzenet {
  szerep: 'user' | 'assistant';
  szoveg: string;
  kartyak?: { id: string; osszegzes: string; allapot?: 'fuggo' | 'fut' | 'kesz' | 'elvetve' | 'hiba'; uzenet?: string }[];
  /** A frissítés megszakította a kérdést – újrakérdezhető (a kérdés szövege). */
  ujrakerdez?: string;
  navigacio?: { ut: string; cim: string }[];
  hiba?: boolean;
}

const TAROLO = 'konrad-beszelgetes';

@Component({
  selector: 'app-konrad',
  templateUrl: './konrad.component.html',
  styleUrls: ['./konrad.component.scss'],
})
export class KonradComponent implements OnChanges {
  /** Az app-keret bal menüje (a navigáció csak ezek közé mehet). */
  @Input() oldalak: any[] = [];
  /** Be van-e jelentkezve a felhasználó (különben nem kérdezünk semmit). */
  @Input() bejelentkezve = false;

  @ViewChild('lista') lista?: ElementRef<HTMLDivElement>;

  elerheto = false;
  nyitva = false;
  fut = false;
  kerdes = '';
  uzenetek: Uzenet[] = [];
  private intezmeny = '';
  /** Az aktuális alrendszer (SULINFORM, NAPLO, BER) – ehhez kérünk szerepet a kiszolgálótól. */
  mod = 'SULINFORM';
  private futo: Subscription | null = null;
  private ellenorizve = '';
  /** A válasz folyamatosan (szavanként) jön-e – az admin beállítása (Admin → Konrád nyuszi). */
  private folyamatos = true;
  /** A most íródó (folyamatos) válasz buboréka, és amit épp utánanéz (eszköz). */
  elo: Uzenet | null = null;
  eszkozJel = '';

  /*  Konrád arcai (a CalciPath-ban megrajzolt figura átlátszó változatai): alapból vidám, amíg
   *  dolgozik, csukott szemmel „gondolkodik”, hibánál csodálkozik. */
  readonly kepek = {
    vidam: 'assets/konrad/vidam.png',
    gondolkodik: 'assets/konrad/gondolkodik.png',
    csodalkozik: 'assets/konrad/csodalkozik.png',
  };
  get arc(): string {
    if (this.fut) return this.kepek.gondolkodik;
    const utolso = this.uzenetek[this.uzenetek.length - 1];
    return utolso?.hiba ? this.kepek.csodalkozik : this.kepek.vidam;
  }

  private static readonly JAVASLATOK: Record<string, string[]> = {
    SULINFORM: [
      'Hogyan kell helyettesítést bejelenteni?',
      'Írd be a mai jelenlétemet 8–16 órára',
      'Mi az órarendem holnap?',
      'Hány beíratlan órám van ebben a hónapban?',
    ],
    NAPLO: [
      'Milyen bevallások vannak még hátra ebben a hónapban?',
      'Rendben van az előző havi ÁFA ennél az ügyfélnél?',
      'Hogyan húzom be a számlákat a NAV-tól?',
      'Hol tudom beküldeni az eÁFA-bevallást?',
    ],
    BER: [
      'Hogyan számfejtem le a hónapot?',
      'Mennyi volt a múlt havi nettó bér összesen?',
      'Hogyan veszek fel új munkavállalót?',
      'Hol küldöm be a 08-as bevallást?',
    ],
  };
  get javaslatok(): string[] { return KonradComponent.JAVASLATOK[this.mod] || KonradComponent.JAVASLATOK['SULINFORM']; }
  get udvozles(): string {
    return this.mod === 'SULINFORM'
      ? 'Szia! Konrád vagyok. Megmutatom, hol mit találsz, és elő is készítem neked a dolgokat – te csak jóváhagyod.'
      : `Szia! Konrád vagyok. Megmutatom, hol mit találsz a ${this.mod === 'NAPLO' ? 'Könyvelésben' : 'Bérszámfejtésben'}, és utánanézek az adatoknak a kiválasztott ügyfélnél.`;
  }
  /** Alrendszerenként külön beszélgetés (a könyvelős kérdés ne kerüljön a tanári előzménybe). */
  private get tarolo(): string { return this.mod === 'SULINFORM' ? TAROLO : `${TAROLO}-${this.mod}`; }

  constructor(private http: HttpClient, private data: DataService, private router: Router, private ref: ChangeDetectorRef,
              private alert: AlertController, private keret: KeretService, private naplo: NaploService,
              private naploMenu: NaploMenuService, private ber: BerService) {
    this.betoltBeszelgetes();
    try { this.nyitva = localStorage.getItem(`${TAROLO}-nyitva`) === '1' && this.uzenetek.length > 0; } catch (_) { /* tiltva */ }
    //  Intézmény- és alrendszerváltáskor újra meg kell nézni, be van-e kapcsolva.
    this.router.events.pipe(filter((e) => e instanceof NavigationEnd)).subscribe(() => this.allapot());
  }

  /** Az aktuális alrendszer beszélgetésének visszaolvasása. */
  private betoltBeszelgetes() {
    try { this.uzenetek = JSON.parse(localStorage.getItem(this.tarolo) || '[]'); } catch (_) { this.uzenetek = []; }
    //  Ha a legutóbbi üzenet egy megválaszolatlan kérdés, a frissítés szakította meg.
    const utolso = this.uzenetek[this.uzenetek.length - 1];
    if (utolso?.szerep === 'user') {
      this.uzenetek.push({ szerep: 'assistant', szoveg: 'A frissítés megszakította a válaszomat. Kérdezzem újra?', hiba: true, ujrakerdez: utolso.szoveg });
      this.ment();
    }
  }

  ngOnChanges() { this.allapot(); }

  private async allapot() {
    if (!this.bejelentkezve) { this.elerheto = false; return; }
    const si = await this.data.getFromStorage('sajatIntezmeny').catch(() => null);
    const intezmeny = String(si?.intezmenyId || '').replace(/^\/+|\/+$/g, '');
    const mod = String(this.keret.a?.aktualisMod() || 'SULINFORM').toUpperCase();
    const kulcs = `${intezmeny}|${mod}`;
    if (!intezmeny || kulcs === this.ellenorizve) return;
    this.ellenorizve = kulcs;
    this.intezmeny = intezmeny;
    if (mod !== this.mod) {
      //  Alrendszerváltás: a futó kérdés megszakad, és az ottani beszélgetés jön elő.
      this.megszakit();
      this.mod = mod;
      this.betoltBeszelgetes();
    }
    try {
      const r: any = await firstValueFrom(this.http.get(`/api/konrad/allapot/${intezmeny}`));
      this.elerheto = !!r?.bekapcsolva;
      this.folyamatos = r?.folyamatos !== false;
    } catch (_) { this.elerheto = false; }
    this.ref.detectChanges();
  }

  /** A menü laposítva: cím + útvonal (a csoportfejlécek nélkül). A Könyvelés és a Bér saját
   *  főmenüjéből jön (jog szerint szűrve, a menüpont leírásával), a többi alrendszerben a bal sávból. */
  private async menu(): Promise<{ title: string; url: string; leiras?: string }[]> {
    const utvonal = (ut: string, query?: Record<string, string>) =>
      query && Object.keys(query).length ? `${ut}?${new URLSearchParams(query).toString()}` : ut;
    if (this.mod === 'NAPLO') {
      if (!this.naploMenu.lathato$.value.length) await this.naploMenu.frissit().catch(() => {});
      const mod = this.naplo.aktiv.konyveles_mod;
      return this.naploMenu.lathato$.value.flatMap((x) => x.pontok.filter((p) => !p.alrendszer).map((p) => {
        const c = menupontCel(p, mod);
        return { title: `${x.f.cim} › ${p.cim}`, url: utvonal(c.ut, c.query), leiras: p.leiras };
      }));
    }
    if (this.mod === 'BER') {
      let szabad: (u: string) => boolean = () => false;
      try { szabad = (await this.data.hozzaferesek()).szabad; } catch (_) { /* jog nélkül üres */ }
      const a = this.ber.aktiv;
      return BER_FOMENUK.flatMap((f) => f.pontok.filter((p) => szabad(p.jog) && lathatoPont(p, a))
        .map((p) => ({ title: `${f.cim} › ${p.cim}`, url: utvonal(p.ut, p.query), leiras: p.leiras })));
    }
    const ki: { title: string; url: string }[] = [];
    const bejar = (lista: any[]) => {
      for (const m of lista || []) {
        const url = String(m?.url || '');
        if (m?.title && url && !url.startsWith('0/') && !m.external) ki.push({ title: String(m.title), url: url.startsWith('/') ? url : `/${url}` });
        if (Array.isArray(m?.submenu)) bejar(m.submenu);
      }
    };
    bejar(this.oldalak);
    return ki;
  }

  /** A nyuszi-gomb: kinyitja / félreteszi az ablakot – a beszélgetés megmarad. */
  nyit() {
    this.nyitva = !this.nyitva;
    try { localStorage.setItem(`${TAROLO}-nyitva`, this.nyitva ? '1' : '0'); } catch (_) { /* tiltva */ }
    if (this.nyitva) setTimeout(() => this.gorget(), 50);
  }

  /** Az X: megerősítés után bezár, megszakítja a gondolkodást és elfelejti a beszélgetést. */
  async bezar() {
    if (this.uzenetek.length || this.fut) {
      const a = await this.alert.create({
        header: 'Bezárod Konrádot?',
        message: 'A beszélgetés törlődik, és ha épp gondolkodik, abbahagyja.',
        buttons: [{ text: 'Mégse', role: 'cancel' }, { text: 'Bezárás', role: 'confirm' }],
      });
      await a.present();
      const { role } = await a.onDidDismiss();
      if (role !== 'confirm') return;
    }
    this.megszakit();
    this.uzenetek = [];
    this.ment();
    this.nyitva = false;
    try { localStorage.setItem(`${TAROLO}-nyitva`, '0'); } catch (_) { /* tiltva */ }
    this.ref.detectChanges();
  }

  /** A futó kérdés megszakítása (a kérés bontása a kiszolgálón is leállítja a modellt). */
  private megszakit() {
    if (this.futo) { this.futo.unsubscribe(); this.futo = null; }
    this.fut = false;
    this.elo = null;
    this.eszkozJel = '';
  }

  async kuld(szoveg?: string) {
    const kerdes = String(szoveg ?? this.kerdes).trim();
    if (!kerdes || this.fut) return;
    const elozmeny = this.uzenetek.filter((u) => !u.hiba).slice(-12).map((u) => ({ szerep: u.szerep, szoveg: u.szoveg }));
    this.uzenetek.push({ szerep: 'user', szoveg: kerdes });
    this.kerdes = '';
    this.fut = true;
    this.ment();
    setTimeout(() => this.gorget(), 30);
    const vege = () => {
      this.fut = false;
      this.futo = null;
      this.ment();
      this.ref.detectChanges();
      setTimeout(() => this.gorget(), 30);
    };
    const menu = await this.menu();
    //  NAPLO / BÉR: a kiválasztott ügyfél (csak az azonosítója – a nevét a kiszolgáló olvassa ki).
    const ugyfel = this.mod === 'NAPLO' ? { id: this.naplo.aktiv.ugyfelId, ev: this.naplo.aktiv.ev }
      : this.mod === 'BER' ? { id: this.ber.aktiv.ugyfelId, ev: this.ber.aktiv.ev, ho: this.ber.aktiv.ho } : null;
    if (!this.fut) return;   // közben megszakították
    const torzs = { kerdes, elozmeny, oldal: this.router.url, menu, mod: this.mod, ugyfel };
    const kesz = (r: any) => {
      const u: Uzenet = this.elo || { szerep: 'assistant', szoveg: '' };
      Object.assign(u, { szoveg: r?.valasz || '', kartyak: (r?.kartyak || []).map((k: any) => ({ ...k, allapot: 'fuggo' })), navigacio: r?.navigacio || [] });
      if (!this.elo) this.uzenetek.push(u);
    };
    const hiba = (uzenet: string) => {
      if (this.elo) this.uzenetek.splice(this.uzenetek.indexOf(this.elo), 1);
      this.uzenetek.push({ szerep: 'assistant', szoveg: uzenet || 'Most nem érem el a szervert – próbáld újra kicsit később.', hiba: true, ujrakerdez: kerdes });
    };
    const lezar = () => { this.elo = null; this.eszkozJel = ''; vege(); };
    if (!this.folyamatos) {
      //  Feliratkozás (nem Promise), hogy megszakítható legyen.
      this.futo = this.http.post(`/api/konrad/cseveg/${this.intezmeny}`, torzs).subscribe({
        next: (r: any) => { kesz(r); lezar(); },
        error: (e: any) => { hiba(e?.error?.error); lezar(); },
      });
      return;
    }
    /*  FOLYAMATOS VÁLASZ: a kiszolgáló soronként egy JSON-t küld (konrad.js) – a szöveg darabjai úgy
     *  jelennek meg, ahogy a modell írja. A HttpClient letöltési eseménye a teljes eddigi szöveget adja
     *  (`partialText`); mindig csak az új, teljes sorokat dolgozzuk fel. */
    let feldolgozva = 0;
    let lezarva = false;
    const sorok = (szoveg: string, mind = false) => {
      const vegIg = mind ? szoveg.length : szoveg.lastIndexOf('\n') + 1;
      if (vegIg <= feldolgozva) return;
      const uj = szoveg.slice(feldolgozva, vegIg);
      feldolgozva = vegIg;
      for (const l of uj.split('\n')) {
        if (!l.trim()) continue;
        let d: any;
        try { d = JSON.parse(l); } catch { continue; }
        if (d.t === 'szoveg') {
          if (!this.elo) { this.elo = { szerep: 'assistant', szoveg: '' }; this.uzenetek.push(this.elo); }
          this.elo.szoveg += d.d || '';
          this.eszkozJel = '';
        } else if (d.t === 'kor') {
          //  Új kör az eszközök után: az előző kör („megnézem…”) szövege helyére a végleges jön.
          if (this.elo) { this.uzenetek.splice(this.uzenetek.indexOf(this.elo), 1); this.elo = null; }
        } else if (d.t === 'eszkoz') {
          this.eszkozJel = 'Utánanézek…';
        } else if (d.t === 'vege') { kesz(d); lezarva = true; }
        else if (d.t === 'hiba') { hiba(d.error); lezarva = true; }
        else if (d.valasz !== undefined) { kesz(d); lezarva = true; }          // a folyamatos mód ki van kapcsolva
      }
      this.ref.detectChanges();
      this.gorget();
    };
    this.futo = this.http.post(`/api/konrad/cseveg/${this.intezmeny}`, { ...torzs, folyam: true },
      { observe: 'events', reportProgress: true, responseType: 'text' }).subscribe({
      next: (ev) => {
        if (ev.type === HttpEventType.DownloadProgress) sorok((ev as HttpDownloadProgressEvent).partialText || '');
        else if (ev.type === HttpEventType.Response) {
          sorok(String(ev.body || ''), true);
          if (!lezarva) hiba('A válasz megszakadt – kérdezd újra.');
          lezar();
        }
      },
      error: (e: any) => {
        let uzenet = '';
        try { uzenet = JSON.parse(String(e?.error || ''))?.error || ''; } catch { /* nem JSON */ }
        hiba(uzenet);
        lezar();
      },
    });
  }

  /** A megszakadt kérdés újraküldése (a jelzés helyére). */
  ujraKerdez(u: Uzenet) {
    const k = u.ujrakerdez;
    if (!k || this.fut) return;
    //  A megszakadt kérdés és a jelzés kikerül – a kuld újra felveszi a kérdést.
    const i = this.uzenetek.indexOf(u);
    if (i > 0 && this.uzenetek[i - 1]?.szerep === 'user' && this.uzenetek[i - 1].szoveg === k) this.uzenetek.splice(i - 1, 2);
    else this.uzenetek.splice(i, 1);
    this.kuld(k);
  }

  async jovahagy(k: any) {
    if (k.allapot !== 'fuggo') return;
    k.allapot = 'fut';
    try {
      await firstValueFrom(this.http.post(`/api/konrad/javaslat/${k.id}/jovahagy/${this.intezmeny}`, {}));
      k.allapot = 'kesz';
      k.uzenet = 'Kész, elmentve.';
    } catch (e: any) {
      k.allapot = 'hiba';
      k.uzenet = e?.error?.error || 'Nem sikerült végrehajtani.';
    }
    this.ment();
    this.ref.detectChanges();
  }

  async elvet(k: any) {
    if (k.allapot !== 'fuggo') return;
    k.allapot = 'elvetve';
    this.http.post(`/api/konrad/javaslat/${k.id}/elvet/${this.intezmeny}`, {}).subscribe({ error: () => {} });
    this.ment();
  }

  odamegy(n: { ut: string }) {
    this.router.navigateByUrl(n.ut);
    //  Kis kijelzőn az ablak eltakarná a képernyőt, ahová vittük.
    if (window.innerWidth < 768) this.nyitva = false;
  }

  /** Új beszélgetés: a futó gondolkodást azonnal megszakítja, és tiszta lappal indul. */
  uj() {
    this.megszakit();
    this.uzenetek = [];
    this.ment();
    this.ref.detectChanges();
  }

  billentyu(ev: KeyboardEvent) {
    if (ev.key === 'Enter' && !ev.shiftKey) { ev.preventDefault(); this.kuld(); }
  }

  /** Egyszerű, biztonságos formázás: félkövér és számozott/felsorolt sorok (HTML-t nem fogadunk el). */
  formaz(s: string): string {
    const esc = String(s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    return esc.replace(/\*\*(.+?)\*\*/g, '<b>$1</b>').replace(/\n/g, '<br>');
  }

  private ment() {
    try { localStorage.setItem(this.tarolo, JSON.stringify(this.uzenetek.slice(-40))); } catch (_) { /* tele / tiltva */ }
  }

  private gorget() {
    const el = this.lista?.nativeElement;
    if (el) el.scrollTop = el.scrollHeight;
  }
}
